import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import {
  Gamepad2,
  Users,
  MessageCircle,
  Bot,
  ArrowRight,
  Play,
  Lock,
  Languages
} from 'lucide-react'

const LandingPage = ({ onGetStarted }) => {
  const { t, i18n } = useTranslation('landing')

  // Flag SVG components
  const USFlag = () => (
    <svg className="w-6 h-4 rounded-sm" viewBox="0 0 60 40">
      <rect width="60" height="40" fill="#B22234"/>
      <rect width="60" height="3.08" y="3.08" fill="white"/>
      <rect width="60" height="3.08" y="9.23" fill="white"/>
      <rect width="60" height="3.08" y="15.38" fill="white"/>
      <rect width="60" height="3.08" y="21.54" fill="white"/>
      <rect width="60" height="3.08" y="27.69" fill="white"/>
      <rect width="60" height="3.08" y="33.85" fill="white"/>
      <rect width="24" height="21.54" fill="#3C3B6E"/>
    </svg>
  )

  const ESFlag = () => (
    <svg className="w-6 h-4 rounded-sm" viewBox="0 0 60 40">
      <rect width="60" height="40" fill="#AA151B"/>
      <rect width="60" height="20" y="10" fill="#F1BF00"/>
    </svg>
  )

  const FRFlag = () => (
    <svg className="w-6 h-4 rounded-sm" viewBox="0 0 60 40">
      <rect width="20" height="40" fill="#002395"/>
      <rect width="20" height="40" x="20" fill="white"/>
      <rect width="20" height="40" x="40" fill="#ED2939"/>
    </svg>
  )

  const DEFlag = () => (
    <svg className="w-6 h-4 rounded-sm" viewBox="0 0 60 40">
      <rect width="60" height="13.33" fill="#000"/>
      <rect width="60" height="13.33" y="13.33" fill="#DD0000"/>
      <rect width="60" height="13.33" y="26.67" fill="#FFCE00"/>
    </svg>
  )

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang)
  }

  const features = [
    {
      icon: Gamepad2,
      title: t('features.multiplayer.title'),
      description: t('features.multiplayer.description')
    },
    {
      icon: MessageCircle,
      title: t('features.chat.title'),
      description: t('features.chat.description')
    },
    {
      icon: Users,
      title: t('features.lobbies.title'),
      description: t('features.lobbies.description')
    },
    {
      icon: Bot,
      title: t('features.bots.title'),
      description: t('features.bots.description')
    }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900">
      {/* Language Selector */}
      <div className="absolute top-4 right-4 z-50">
        <div className="flex items-center gap-2 bg-black/20 backdrop-blur-sm rounded-lg p-2">
          <Languages className="w-4 h-4 text-gray-300" />
          <div className="flex gap-1">
            <button
              onClick={() => changeLanguage('en')}
              className={`px-3 py-2 rounded text-sm font-medium transition-colors flex items-center gap-1 ${
                i18n.language === 'en'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-300 hover:text-white hover:bg-white/10'
              }`}
              title={t('language.english')}
            >
              <USFlag />
              <span className="text-sm font-medium">EN</span>
            </button>
            <button
              onClick={() => changeLanguage('es')}
              className={`px-3 py-2 rounded text-sm font-medium transition-colors flex items-center gap-1 ${
                i18n.language === 'es'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-300 hover:text-white hover:bg-white/10'
              }`}
              title="Español"
            >
              <ESFlag />
              <span className="text-sm font-medium">ES</span>
            </button>
            <button
              onClick={() => changeLanguage('fr')}
              className={`px-3 py-2 rounded text-sm font-medium transition-colors flex items-center gap-1 ${
                i18n.language === 'fr'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-300 hover:text-white hover:bg-white/10'
              }`}
              title="Français"
            >
              <FRFlag />
              <span className="text-sm font-medium">FR</span>
            </button>
            <button
              onClick={() => changeLanguage('de')}
              className={`px-3 py-2 rounded text-sm font-medium transition-colors flex items-center gap-1 ${
                i18n.language === 'de'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-300 hover:text-white hover:bg-white/10'
              }`}
              title={t('language.german')}
            >
              <DEFlag />
              <span className="text-sm font-medium">DE</span>
            </button>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="relative overflow-hidden min-h-[640px] flex items-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/lions-den-hero.jpg')" }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/70 to-gray-900"></div>

        <div className="relative px-4 py-20 mx-auto max-w-7xl sm:py-24 lg:py-32 w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="text-5xl font-bold text-white sm:text-6xl lg:text-7xl"
            >
              {t('hero.title')}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
              className="mx-auto mt-6 max-w-2xl text-xl text-gray-300 sm:text-2xl"
            >
              {t('hero.subtitle')}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.8 }}
              className="flex flex-col sm:flex-row gap-4 justify-center mt-10"
            >
              <button
                onClick={onGetStarted}
                className="group relative px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-2xl hover:shadow-blue-500/25"
              >
                <span className="relative flex items-center justify-center gap-2">
                  <Play className="w-5 h-5" />
                  {t('hero.getStarted')}
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </span>
              </button>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-20 px-4 mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-white mb-4">
            {t('features.title')}
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            {t('features.subtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2 + index * 0.1, duration: 0.6 }}
              className="group h-full"
            >
              <div className="glass-effect p-6 rounded-xl text-center hover:scale-105 transition-all duration-300 hover:shadow-2xl hover:shadow-blue-500/10 h-full flex flex-col">
                <div className="relative inline-block mb-4 self-center">
                  <div className="absolute inset-0 bg-blue-500 rounded-full blur-lg opacity-0 group-hover:opacity-30 transition-opacity duration-300"></div>
                  <div className="relative bg-gradient-to-r from-blue-600 to-purple-600 p-4 rounded-full">
                    <feature.icon className="w-8 h-8 text-white" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-400 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Tiers Section — explains guest (basic) vs. signed-in (premium)
          access. Deliberately generic/conceptual copy, not a live game
          list: this page renders before login, when there's no socket
          connection yet to fetch the current tier assignments from
          games/gameTiers.js. */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.8, duration: 0.8 }}
        className="py-20 px-4"
      >
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              {t('tiers.title')}
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              {t('tiers.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 2, duration: 0.6 }}
              className="group h-full"
            >
              <div className="glass-effect p-6 rounded-xl text-center hover:scale-105 transition-all duration-300 hover:shadow-2xl hover:shadow-blue-500/10 h-full flex flex-col">
                <div className="relative inline-block mb-4 self-center">
                  <div className="absolute inset-0 bg-blue-500 rounded-full blur-lg opacity-0 group-hover:opacity-30 transition-opacity duration-300"></div>
                  <div className="relative bg-gradient-to-r from-blue-600 to-purple-600 p-4 rounded-full">
                    <Play className="w-8 h-8 text-white" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">
                  {t('tiers.basic.title')}
                </h3>
                <p className="text-gray-400 leading-relaxed">
                  {t('tiers.basic.description')}
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 2.1, duration: 0.6 }}
              className="group h-full"
            >
              <div className="glass-effect p-6 rounded-xl text-center hover:scale-105 transition-all duration-300 hover:shadow-2xl hover:shadow-blue-500/10 h-full flex flex-col">
                <div className="relative inline-block mb-4 self-center">
                  <div className="absolute inset-0 bg-purple-500 rounded-full blur-lg opacity-0 group-hover:opacity-30 transition-opacity duration-300"></div>
                  <div className="relative bg-purple-600 p-4 rounded-full">
                    <Lock className="w-8 h-8 text-white" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">
                  {t('tiers.premium.title')}
                </h3>
                <p className="text-gray-400 leading-relaxed">
                  {t('tiers.premium.description')}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

export default LandingPage